import React, { useState, useRef, useLayoutEffect, useContext, useCallback, useEffect } from "react";
import {
  Typography,
  Box,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery,
  Chip,
  Button,
  CircularProgress,
  Snackbar,
  LinearProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { keyframes } from "@mui/system";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import BookmarkAddedIcon from "@mui/icons-material/BookmarkAdded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import UserContext from "../contexts/userContext";
import "../Recipes/RecipeDetail.css"; // Import the same CSS for instructions styling
import RecipeCard, { getAccentColors } from "../Recipes/RecipeCard";

// Staggered fade-in animation
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Sanitize value for display
function sanitize(value) {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string" && value.toLowerCase() === "null") {
    return "";
  }
  return value;
}

// Format ingredient for display - handles both string and object formats
function formatIngredientName(ingredient) {
  if (typeof ingredient === "string") {
    return ingredient;
  }
  if (typeof ingredient === "object" && ingredient !== null) {
    const name = sanitize(ingredient.name) || "";
    return name.toLowerCase();
  }
  return String(ingredient);
}

function formatIngredientPreparation(ingredient) {
  if (typeof ingredient === "object" && ingredient !== null) {
    return sanitize(ingredient.preparation)?.toLowerCase() || "";
  }
  return "";
}

function formatIngredientQuantity(ingredient) {
  if (typeof ingredient === "object" && ingredient !== null) {
    const qty = ingredient.quantity;
    if (typeof qty === "string") {
      return qty;
    }
    if (typeof qty === "object" && qty !== null) {
      return `${sanitize(qty.value)} ${sanitize(qty.unit)}`.trim();
    }
    return "";
  }
  return "";
}

const DESIRED_SPEED = 200; // em/s - matching RecipeDetail

/**
 * Expandable web result card component using shared RecipeCard
 */
function WebResultCard({
  result,
  recipe,
  index,
  onResultClick,
  isMobile,
  isImported,
  isImporting,
  onImport,
  user,
  savedBadgeVariant,
  savedRecipeId,
}) {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllIngredients, setShowAllIngredients] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [transitionDuration, setTransitionDuration] = useState("0.3s");
  const [activeTab, setActiveTab] = useState(0);
  const contentRef = useRef(null);
  const descriptionRef = useRef(null);

  const MAX_LINES = 5;

  const isDark = theme.palette.mode === "dark";
  const accent = getAccentColors(isDark);

  // Calculate content height for smooth animation - matching RecipeDetail
  const updateContentHeight = useCallback(() => {
    if (contentRef.current) {
      const heightInPx = contentRef.current.scrollHeight;
      setContentHeight(heightInPx);
      const heightInEm = heightInPx / 16;
      const durationInSeconds = heightInEm / DESIRED_SPEED;
      setTransitionDuration(`${durationInSeconds}s`);
    }
  }, []);

  // ResizeObserver for content height changes
  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        updateContentHeight();
      });
      resizeObserver.observe(contentRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [contentRef, updateContentHeight]);

  // Update height when content changes
  useLayoutEffect(() => {
    updateContentHeight();
  }, [recipe, isExpanded, showAllIngredients, updateContentHeight]);

  // Update on window resize
  useEffect(() => {
    window.addEventListener("resize", updateContentHeight);
    return () => {
      window.removeEventListener("resize", updateContentHeight);
    };
  }, [updateContentHeight]);

  // Helper function to get line height
  function getLineHeight(element) {
    const computedStyle = window.getComputedStyle(element);
    let lineHeight = computedStyle.lineHeight;

    if (lineHeight === "normal") {
      const fontSize = parseFloat(computedStyle.fontSize);
      lineHeight = fontSize * 1.2;
    } else {
      lineHeight = parseFloat(lineHeight);
    }

    return lineHeight;
  }

  // Check if description needs truncation
  useLayoutEffect(() => {
    if (descriptionRef.current && isExpanded) {
      const descriptionElement = descriptionRef.current;
      const lineHeight = getLineHeight(descriptionElement);
      const maxHeight = lineHeight * MAX_LINES;

      if (descriptionElement.scrollHeight > maxHeight) {
        setIsDescriptionTruncated(true);
      } else {
        setIsDescriptionTruncated(false);
      }
    } else {
      setIsDescriptionTruncated(false);
    }
  }, [recipe.description, showFullDescription, isExpanded, MAX_LINES]);

  const handleClick = () => {
    if (isMobile) {
      setIsExpanded(!isExpanded);
    } else {
      onResultClick(result.url);
    }
  };

  // Prepare ingredient rows for DataGrid
  const ingredientRows = (recipe.ingredients || []).map((ingredient, idx) => ({
    id: idx,
    name: formatIngredientName(ingredient),
    preparation: formatIngredientPreparation(ingredient),
    quantity: formatIngredientQuantity(ingredient),
  }));

  const hasMoreThanSixIngredients = ingredientRows.length > 6;

  const columns = [
    {
      field: "name",
      headerName: "ingredient",
      flex: 1,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            padding: "8px",
          }}
        >
          <div>{params.row.name}</div>
          {params.row.preparation && (
            <div
              style={{
                fontStyle: "italic",
                color: "gray",
                marginTop: "0.25em",
              }}
            >
              {params.row.preparation}
            </div>
          )}
        </div>
      ),
    },
    {
      field: "quantity",
      headerName: "quantity",
      flex: 0.5,
      minWidth: 145,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            padding: "8px",
          }}
        >
          {params.value}
        </div>
      ),
    },
  ];

  // Check if this recipe is saved in user's catalog
  const isSaved = !!savedRecipeId || isImported;

  // Expanded content for mobile - using same animation pattern as RecipeDetail
  const expandedContent = isMobile && (
    <div
      ref={contentRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        overflow: "hidden",
        transition: `max-height ${transitionDuration} cubic-bezier(0.4, 0, 0.2, 1)`,
        maxHeight: isExpanded
          ? showAllIngredients
            ? "none"
            : `${contentHeight}px`
          : "0px",
        padding: "0 12px",
        backgroundColor: "transparent",
      }}
    >
      {isExpanded && (
        <>
          {/* Description */}
        {recipe.description && (
          <Typography
            variant="body1"
            ref={descriptionRef}
            sx={{
              marginBottom: "1em",
              fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
              fontSize: "13px",
              lineHeight: "22px",
              color: theme.palette.text.primary,
              letterSpacing: "-0.01em",
              opacity: 0.9,
              ...(isDescriptionTruncated && !showFullDescription
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: MAX_LINES,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    position: "relative",
                  }
                : {}),
            }}
          >
            {recipe.description}
            {isDescriptionTruncated && !showFullDescription && (
              <span
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  background: `linear-gradient(to right, transparent, ${isDark ? "#1e1e23" : "#fff"} 20%)`,
                  paddingLeft: "16px",
                  fontWeight: "500",
                  cursor: "pointer",
                  color: accent.primary,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullDescription(true);
                }}
              >
                ...show more
              </span>
            )}
          </Typography>
        )}

        {/* Large Image */}
        {recipe.image_url && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              mt: 1.5,
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                maxWidth: "500px",
                height: "500px",
                borderRadius: "16px",
                border: `1.5px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
                overflow: "hidden",
                alignItems: "center",
                justifyContent: "center",
                display: "flex",
                boxShadow: `0 8px 32px -8px rgba(0,0,0,${isDark ? "0.4" : "0.15"})`,
              }}
            >
              <img
                src={recipe.image_url}
                alt={recipe.title || result.title || ""}
                style={{
                  objectFit: "cover",
                  height: "100%",
                  transform: "scale(1.1)",
                  filter: isDark ? "brightness(0.9)" : "none",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </Box>
          </Box>
        )}

        {/* Tabs */}
        <Box
          sx={{
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
            mb: 2,
            mt: 1,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              e.stopPropagation();
              setActiveTab(newValue);
            }}
            sx={{
              minHeight: 0,
              "& .MuiTabs-indicator": {
                backgroundColor: accent.primary,
                height: "2px",
              },
              "& .MuiTab-root": {
                minHeight: 0,
                padding: "10px 16px",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "0.8rem",
                fontWeight: 500,
                textTransform: "lowercase",
                letterSpacing: "0.01em",
                color: theme.palette.text.secondary,
                transition: "color 0.2s ease",
                "&.Mui-selected": {
                  color: accent.primary,
                  fontWeight: 600,
                },
                "&:hover": {
                  color: theme.palette.text.primary,
                },
              },
            }}
          >
            <Tab label="ingredients" />
            <Tab label="instructions" />
            <Tab label="notes" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box
          key={activeTab}
          sx={{
            animation: "fadeIn 0.2s ease-out",
            "@keyframes fadeIn": {
              "0%": { opacity: 0, transform: "translateY(4px)" },
              "100%": { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {/* Ingredients Tab */}
          {activeTab === 0 && ingredientRows.length > 0 && (
            <Box sx={{ position: "relative", mb: 2 }}>
              <DataGrid
                rows={
                  showAllIngredients || !hasMoreThanSixIngredients
                    ? ingredientRows
                    : ingredientRows.slice(0, 6)
                }
                columns={columns}
                disableRowSelectionOnClick
                disableColumnMenu={true}
                getRowHeight={() => "auto"}
                autoHeight
                sx={{
                  borderRadius: "12px",
                  fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                  fontSize: "12px",
                  border: `1.5px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                  background: isDark
                    ? "linear-gradient(135deg, rgba(30, 30, 35, 0.5) 0%, rgba(20, 20, 25, 0.5) 100%)"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(248, 250, 252, 0.5) 100%)",
                  overflow: "hidden",
                  "& .MuiDataGrid-columnHeader": {
                    color: theme.palette.text.primary,
                    backgroundColor: isDark
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.02)",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: "600",
                    fontSize: "12px",
                    padding: "10px",
                    letterSpacing: "0.02em",
                    textTransform: "lowercase",
                  },
                  "& .MuiDataGrid-columnHeaderDraggableContainer": {
                    width: "fit-content",
                  },
                  "& .MuiDataGrid-row:nth-of-type(odd)": {
                    backgroundColor: isDark
                      ? "rgba(255,255,255,0.02)"
                      : "rgba(0,0,0,0.01)",
                  },
                  "& .MuiDataGrid-row:nth-of-type(even)": {
                    backgroundColor: "transparent",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: isDark
                      ? "rgba(187, 134, 252, 0.05)"
                      : "rgba(99, 102, 241, 0.04)",
                  },
                  "& .MuiDataGrid-cell": {
                    display: "block",
                    lineHeight: "1.5em",
                    padding: "10px",
                    boxSizing: "border-box",
                    borderColor: isDark
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.04)",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    minHeight:
                      showAllIngredients || !hasMoreThanSixIngredients
                        ? 0
                        : "52px",
                    border: "none",
                  },
                  "& .MuiTablePagination-root": {
                    display: "none",
                  },
                }}
              />
              {hasMoreThanSixIngredients && !showAllIngredients && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllIngredients(true);
                  }}
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "52px",
                    background: isDark
                      ? "linear-gradient(135deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.98) 100%)"
                      : "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: accent.primary,
                    fontSize: "12px",
                    fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                    cursor: "pointer",
                    fontWeight: "500",
                    letterSpacing: "0.01em",
                    border: `1.5px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                    borderTop: "none",
                    borderBottomLeftRadius: "12px",
                    borderBottomRightRadius: "12px",
                    zIndex: 2,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDark
                      ? "rgba(187, 134, 252, 0.08)"
                      : "rgba(99, 102, 241, 0.06)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  + show all ingredients
                </div>
              )}
            </Box>
          )}

          {/* Instructions Tab */}
          {activeTab === 1 && recipe.instructions?.length > 0 && (
            <Box
              style={{
                "--primary-color": accent.primary,
                "--secondary-color": accent.secondary,
                "--text-color": theme.palette.text.primary,
                "--font-family": "'JetBrains Mono', 'IBM Plex Mono', monospace",
                "--bg-color": isDark
                  ? "rgba(30, 30, 35, 1)"
                  : "rgba(255, 255, 255, 1)",
              }}
            >
              <ol className="customList">
                {recipe.instructions.map((instruction, idx) => (
                  <li key={idx} className="customListItem">
                    {instruction}
                  </li>
                ))}
              </ol>
            </Box>
          )}

          {/* Notes Tab */}
          {activeTab === 2 && recipe.description && (
            <Box sx={{ height: "100%" }}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                  fontSize: "13px",
                  lineHeight: "22px",
                  color: theme.palette.text.primary,
                  letterSpacing: "-0.01em",
                  opacity: 0.9,
                }}
              >
                {recipe.description}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Action buttons - below tabs */}
        <Box
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            component="a"
            href={result.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{
              fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
              fontSize: "0.75rem",
              color: accent.primary,
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            view full recipe →
          </Typography>

          {/* Save to Catalog button - hide if already saved */}
          {user && !isSaved && (
            <Button
              variant={isImported ? "outlined" : "contained"}
              size="small"
              disabled={isImported || isImporting}
              onClick={(e) => {
                e.stopPropagation();
                onImport(result.url);
              }}
              startIcon={
                isImporting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : isImported ? (
                  <BookmarkAddedIcon />
                ) : (
                  <BookmarkAddIcon />
                )
              }
              sx={{
                fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                fontSize: "0.7rem",
                textTransform: "none",
                minWidth: "auto",
                py: 0.5,
                px: 1.5,
              }}
            >
              {isImporting ? "saving..." : isImported ? "saved" : "save to catalog"}
            </Button>
          )}
          {/* Show saved indicator on mobile if already saved */}
          {user && isSaved && (
            <Chip
              label="in your catalog"
              size="small"
              icon={<CheckCircleIcon />}
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                background: isDark
                  ? "linear-gradient(135deg, rgba(3, 218, 198, 0.2), rgba(3, 218, 198, 0.1))"
                  : "linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.08))",
                color: accent.saved,
                border: `1px solid ${isDark ? "rgba(3, 218, 198, 0.3)" : "rgba(16, 185, 129, 0.25)"}`,
                "& .MuiChip-icon": { fontSize: 14, color: accent.saved },
              }}
            />
          )}
        </Box>
        </>
      )}
    </div>
  );

  return (
    <RecipeCard
      recipe={recipe}
      index={index}
      isOpen={isExpanded}
      onClick={handleClick}
      source={{
        name: result.domainName || "",
        type: "web",
        isTrusted: result.isTrusted || false,
      }}
      saved={{
        isSaved,
        variant: savedBadgeVariant,
      }}
    >
      {expandedContent}
    </RecipeCard>
  );
}

/**
 * Animated web search results with staggered entrance and polished styling
 */
function WebSearchResultsAnimated({
  results,
  loading,
  errorMessage,
  onResultClick,
  recipes = {},
  failedUrls = new Set(),
  savedUrls = {},
  savedBadgeVariant = "chip",
  onRecipeSaved,
  // Find More feature props
  onFindMore,
  isLoadingMore = false,
  hasMoreResults = true,
  targetResultCount = 10,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, session } = useContext(UserContext);

  // Track imported URLs and importing state
  const [importedUrls, setImportedUrls] = useState(new Set());
  const [importingUrl, setImportingUrl] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const isDark = theme.palette.mode === "dark";

  // Handle importing a recipe
  const handleImport = async (url) => {
    if (!user || !session?.access_token) {
      setSnackbar({
        open: true,
        message: "Please sign in to save recipes",
        severity: "warning",
      });
      return;
    }

    setImportingUrl(url);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/import`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ sourceUrl: url }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setImportedUrls((prev) => new Set([...prev, url]));
        if (onRecipeSaved && data.recipeId) {
          onRecipeSaved(url, data.recipeId);
        }
        setSnackbar({
          open: true,
          message: `"${data.title}" saved to your catalog!`,
          severity: "success",
        });
      } else if (response.status === 409) {
        setImportedUrls((prev) => new Set([...prev, url]));
        if (onRecipeSaved && data.recipeId) {
          onRecipeSaved(url, data.recipeId);
        }
        setSnackbar({
          open: true,
          message: data.message || "Recipe already in your catalog",
          severity: "info",
        });
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to save recipe",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Import error:", error);
      setSnackbar({
        open: true,
        message: "Failed to save recipe. Please try again.",
        severity: "error",
      });
    } finally {
      setImportingUrl(null);
    }
  };

  // Check if a result is a placeholder skeleton vs real search result
  const isRealResult = (r) => r.url && !r.url.startsWith("skeleton-");

  // Separate ready and processing results
  const readyResults = results.filter(
    (r) => recipes[r.url] && !failedUrls.has(r.url)
  );
  const processingResults = results.filter(
    (r) => isRealResult(r) && !recipes[r.url] && !failedUrls.has(r.url)
  );

  // Calculate progress for the progress bar
  const realResults = results.filter(isRealResult);
  const totalToProcess = realResults.length;
  const completedCount = readyResults.length + failedUrls.size;
  const progressPercent =
    totalToProcess > 0 ? (completedCount / totalToProcess) * 100 : 0;

  const allProcessed = totalToProcess > 0 && completedCount >= totalToProcess;
  const stuckWithResults =
    !loading && processingResults.length === 0 && readyResults.length > 0;
  const isComplete = allProcessed || stuckWithResults;

  if (errorMessage) {
    return (
      <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
        {errorMessage}
      </Alert>
    );
  }

  // Initial loading state - matching RecipeLoadingSkeleton structure
  if (loading && results.length === 0) {
    const cardBgGradient = isDark
      ? "linear-gradient(135deg, rgba(30, 30, 35, 1) 0%, rgba(20, 20, 25, 1) 100%)"
      : "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 1) 100%)";
    const normalBorder = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
    const skeletonBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";

    return (
      <Box sx={{ pt: 0.5 }}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Box
            key={index}
            sx={{
              background: normalBorder,
              padding: "2px",
              borderRadius: "18px",
              mb: 1.5,
              boxShadow: `0 2px 12px -4px rgba(0,0,0,${isDark ? "0.3" : "0.08"})`,
            }}
          >
            <Box
              sx={{
                background: cardBgGradient,
                borderRadius: "16px",
                overflow: "hidden",
                p: 1.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              {/* Left Column */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton
                  variant="text"
                  width="65%"
                  height={24}
                  sx={{
                    bgcolor: skeletonBg,
                    borderRadius: "6px",
                  }}
                />
                <Box
                  sx={{
                    mt: "6px",
                    display: "flex",
                    gap: 0.5,
                    alignItems: "center",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    width={60}
                    height={22}
                    sx={{
                      bgcolor: skeletonBg,
                      borderRadius: "8px",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    mt: "10px",
                    display: "flex",
                    gap: "14px",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <Skeleton
                      variant="circular"
                      width={15}
                      height={15}
                      sx={{ bgcolor: skeletonBg }}
                    />
                    <Skeleton
                      variant="text"
                      width={20}
                      height={12}
                      sx={{ bgcolor: skeletonBg, borderRadius: "3px" }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <Skeleton
                      variant="circular"
                      width={15}
                      height={15}
                      sx={{ bgcolor: skeletonBg }}
                    />
                    <Skeleton
                      variant="text"
                      width={28}
                      height={12}
                      sx={{ bgcolor: skeletonBg, borderRadius: "3px" }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <Skeleton
                      variant="circular"
                      width={15}
                      height={15}
                      sx={{ bgcolor: skeletonBg }}
                    />
                    <Skeleton
                      variant="text"
                      width={28}
                      height={12}
                      sx={{ bgcolor: skeletonBg, borderRadius: "3px" }}
                    />
                  </Box>
                </Box>
              </Box>
              {/* Right Column - Image placeholder */}
              <Skeleton
                variant="rectangular"
                width={80}
                height={80}
                sx={{
                  bgcolor: skeletonBg,
                  borderRadius: "12px",
                  flexShrink: 0,
                  ml: 2,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  // No results
  if (!loading && results.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
          color: theme.palette.text.secondary,
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace" }}
        >
          no web results found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 0.5 }}>
      {/* Progress indicator */}
      {totalToProcess > 0 && (
        <Box
          sx={{
            mb: 1.5,
            animation: `${fadeInUp} 0.3s ease forwards`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                fontSize: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {isComplete ? (
                <>
                  <CheckCircleIcon
                    sx={{
                      fontSize: 14,
                      color: theme.palette.success.main,
                    }}
                  />
                  {readyResults.length === totalToProcess
                    ? `all ${readyResults.length} recipes loaded`
                    : `${readyResults.length} recipes loaded`}
                </>
              ) : (
                <>
                  {readyResults.length} of {totalToProcess} recipes ready
                </>
              )}
            </Typography>
            {!isComplete && processingResults.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.3,
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.primary.main,
                        opacity: 0.5,
                        animation: `${keyframes`
                          0%, 100% { opacity: 0.3; }
                          50% { opacity: 1; }
                        `} 1s ease-in-out infinite`,
                        animationDelay: `${i * 0.15}s`,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 3,
              borderRadius: 1.5,
              backgroundColor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.06)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 1.5,
                backgroundColor: isComplete
                  ? theme.palette.success.main
                  : theme.palette.primary.main,
                transition: "transform 0.3s ease, background-color 0.3s ease",
              },
            }}
          />
        </Box>
      )}

      {/* Ready results with staggered animation */}
      {readyResults.map((result, index) => {
        const recipe = recipes[result.url];
        if (!recipe) return null;

        return (
          <WebResultCard
            key={result.url}
            result={result}
            recipe={recipe}
            index={index}
            onResultClick={onResultClick}
            isMobile={isMobile}
            isImported={importedUrls.has(result.url)}
            isImporting={importingUrl === result.url}
            onImport={handleImport}
            user={user}
            savedBadgeVariant={savedBadgeVariant}
            savedRecipeId={savedUrls[result.url]}
          />
        );
      })}

      {/* Processing results - show title placeholder while loading */}
      {processingResults.map((result, index) => {
        // Extract domain from URL for display
        let domain = "";
        try {
          domain = new URL(result.url).hostname.replace("www.", "");
        } catch (e) {
          domain = "";
        }

        const cardBgGradient = isDark
          ? "linear-gradient(135deg, rgba(30, 30, 35, 0.7) 0%, rgba(20, 20, 25, 0.7) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(248, 250, 252, 0.7) 100%)";
        const normalBorder = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
        const skeletonBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";

        return (
          <Box
            key={result.url}
            sx={{
              background: normalBorder,
              padding: "2px",
              borderRadius: "18px",
              mb: 1.5,
              boxShadow: `0 2px 12px -4px rgba(0,0,0,${isDark ? "0.2" : "0.06"})`,
              opacity: 0.7,
              animation: `${fadeInUp} 0.4s ease-out`,
              animationDelay: `${(readyResults.length + index) * 0.05}s`,
              animationFillMode: "both",
            }}
          >
            <Box
              sx={{
                background: cardBgGradient,
                borderRadius: "16px",
                p: 2,
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              {/* Left Column - Content with real title */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Title from web search result */}
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                    mb: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {result.title || "Loading recipe..."}
                </Typography>

                {/* Domain */}
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                    fontSize: "0.75rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  {domain}
                </Typography>

                {/* Loading indicator */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <CircularProgress
                    size={14}
                    thickness={4}
                    sx={{
                      color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                      fontSize: "0.7rem",
                      fontStyle: "italic",
                    }}
                  >
                    Extracting recipe...
                  </Typography>
                </Box>
              </Box>

              {/* Right Column - Image placeholder skeleton */}
              <Skeleton
                variant="rectangular"
                width={80}
                height={80}
                sx={{
                  bgcolor: skeletonBg,
                  borderRadius: "12px",
                  flexShrink: 0,
                }}
              />
            </Box>
          </Box>
        );
      })}

      {/* Find More Button - shown when all processing is done but results are below target */}
      {(() => {
        // Calculate if we should show "Find More"
        const doneProcessing = processingResults.length === 0 && !loading;
        const belowTarget = readyResults.length < targetResultCount;
        const showFindMore = doneProcessing && belowTarget && hasMoreResults && onFindMore;
        
        if (!showFindMore) return null;
        
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 3,
              animation: `${fadeInUp} 0.4s ease-out`,
            }}
          >
            <Button
              variant="outlined"
              onClick={onFindMore}
              disabled={isLoadingMore}
              startIcon={
                isLoadingMore ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SearchIcon />
                )
              }
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1,
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 500,
                borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
                color: isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.8)",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: isDark 
                    ? "rgba(255,255,255,0.05)" 
                    : "rgba(0,0,0,0.03)",
                },
                "&.Mui-disabled": {
                  borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
                  color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                },
              }}
            >
              {isLoadingMore 
                ? "Finding more recipes..." 
                : `Find More Recipes (${readyResults.length} found)`
              }
            </Button>
          </Box>
        );
      })()}

      {/* No more results message */}
      {(() => {
        const doneProcessing = processingResults.length === 0 && !loading;
        const showNoMore = doneProcessing && !hasMoreResults && readyResults.length > 0 && readyResults.length < targetResultCount;
        
        if (!showNoMore) return null;
        
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 2,
              animation: `${fadeInUp} 0.4s ease-out`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                fontStyle: "italic",
              }}
            >
              No more results available
            </Typography>
          </Box>
        );
      })()}

      {/* Import success/error snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default WebSearchResultsAnimated;
